import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
    PlusIcon,
    CalendarDaysIcon,
    MapPinIcon,
    CloudIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon,
    ClockIcon,
    EyeIcon,
    PencilIcon,
    TrashIcon,
    ArrowPathIcon,
    FunnelIcon
} from '@heroicons/react/24/outline';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';
import { fetchFarmPlots } from '../utils/activityApi';
import type { FarmField } from '../components/activities/types';
import FieldDiaryForm from '../components/field-diary/FieldDiaryForm';
import FieldDiaryDetail from '../components/field-diary/FieldDiaryDetail';

interface FieldDiaryEntry {
    id: number;
    scout_id: number;
    scout_name: string;
    cohort_id?: number;
    cohort_name?: string;
    field_plot_id?: number | null;
    field_name?: string | null;
    field_code?: string | null;
    field_crop_type?: string | null;
    farmer_name?: string | null;
    visit_date: string;
    visit_type: string;
    location_name?: string;
    location_lat?: number;
    location_lng?: number;
    weather_condition?: string;
    temperature_celsius?: number;
    humidity_percent?: number;
    crop_type?: string;
    growth_stage?: string;
    health_status: string;
    health_score?: number;
    pest_observed: boolean;
    pest_type?: string;
    disease_observed: boolean;
    disease_type?: string;
    severity_level?: string;
    activities?: string[];
    observations?: string;
    recommendations?: string;
    action_items?: string;
    photos?: any[];
    follow_up_required: boolean;
    follow_up_date?: string;
    follow_up_notes?: string;
    status: string;
    created_at: string;
}

interface DiarySummary {
    total_entries: number;
    unique_scouts: number;
    cohorts_visited: number;
    avg_health_score: number;
    pest_observations: number;
    disease_observations: number;
    follow_ups_needed: number;
    emergency_visits: number;
    critical_health: number;
    poor_health: number;
    good_health: number;
    excellent_health: number;
}

const HEALTH_STATUS_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
    excellent: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
    good: { bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500' },
    fair: { bg: 'bg-yellow-50', text: 'text-yellow-700', dot: 'bg-yellow-500' },
    poor: { bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-500' },
    critical: { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
};

const VISIT_TYPE_ICONS: Record<string, string> = {
    routine: '🔍',
    followup: '🔄',
    emergency: '🚨',
    training: '📚',
};

const FieldDiaryPage: React.FC = () => {
    const { user } = useAuth();
    
    const [entries, setEntries] = useState<FieldDiaryEntry[]>([]);
    const [summary, setSummary] = useState<DiarySummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState<string | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [selectedEntry, setSelectedEntry] = useState<FieldDiaryEntry | null>(null);
    const [editingEntry, setEditingEntry] = useState<FieldDiaryEntry | null>(null);
    const [fields, setFields] = useState<FarmField[]>([]);
    
    // Filters
    const [filters, setFilters] = useState({
        start_date: '',
        end_date: '',
        health_status: '',
        visit_type: '',
        status: '',
        field_plot_id: ''
    });

    const canRecordVisits = ['field_scout', 'field_facilitator', 'agronomist', 'agronomist_advisor'].includes(user?.role || '');
    const isManager = ['project_manager', 'tenant_admin', 'business_manager', 'field_manager'].includes(user?.role || '');

    // Field filter options come from the same authorized field list the form
    // uses, so a user can never filter by a field they cannot record on.
    useEffect(() => {
        fetchFarmPlots()
            .then(setFields)
            .catch(() => setFields([]));
    }, []);

    useEffect(() => {
        fetchData();
    }, [filters]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [entriesRes, summaryRes] = await Promise.all([
                apiGet<{ entries: FieldDiaryEntry[] }>('/api/field-diary', filters),
                apiGet<{ summary: DiarySummary }>('/api/field-diary/summary', filters)
            ]);
            setEntries(entriesRes.entries || []);
            setSummary(summaryRes.summary || null);
            setFetchError(null);
        } catch (error) {
            console.error('Failed to fetch field diary data:', error);
            setFetchError(error instanceof Error ? error.message : 'Visit records could not be loaded.');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateEntry = async (data: any) => {
        try {
            await apiPost('/api/field-diary', data);
            setShowForm(false);
            fetchData();
        } catch (error) {
            console.error('Failed to create entry:', error);
            throw error;
        }
    };

    const handleUpdateEntry = async (data: any) => {
        if (!editingEntry) return;
        try {
            await apiPut(`/api/field-diary/${editingEntry.id}`, data);
            setEditingEntry(null);
            setSelectedEntry(null);
            fetchData();
        } catch (error) {
            console.error('Failed to update entry:', error);
            throw error;
        }
    };

    const handleDeleteEntry = async (id: number) => {
        if (!window.confirm('Are you sure you want to delete this entry?')) return;
        try {
            await apiDelete(`/api/field-diary/${id}`);
            setSelectedEntry(null);
            fetchData();
        } catch (error) {
            console.error('Failed to delete entry:', error);
        }
    };

    const getHealthColor = (status: string) => {
        return HEALTH_STATUS_COLORS[status] || HEALTH_STATUS_COLORS.good;
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                                <CalendarDaysIcon className="w-7 h-7 text-emerald-600" />
                                {canRecordVisits ? 'Field Visits' : 'Visit Reports'}
                            </h1>
                            <p className="text-sm text-gray-500 mt-1">
                                {canRecordVisits ? 'Record and follow up on field observations.' : 'Review visits submitted by your field team.'}
                            </p>
                        </div>
                        {canRecordVisits && (
                            <button
                                onClick={() => setShowForm(true)}
                                className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-cyan-600 text-white font-semibold rounded-xl hover:from-emerald-600 hover:to-cyan-700 shadow-lg flex items-center gap-2"
                            >
                                <PlusIcon className="w-5 h-5" />
                                New Entry
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {fetchError && (
                    <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950" role="alert">
                        <span>{fetchError}</span>
                        <button onClick={() => void fetchData()} className="inline-flex items-center gap-2 font-semibold text-amber-900 hover:underline">
                            <ArrowPathIcon className="h-4 w-4" /> Retry
                        </button>
                    </div>
                )}
                {/* Summary Cards */}
                {summary && (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                            <p className="text-xs font-medium text-gray-500">Total Entries</p>
                            <p className="text-2xl font-bold text-gray-900">{summary.total_entries}</p>
                        </div>
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                            <p className="text-xs font-medium text-gray-500">Active Scouts</p>
                            <p className="text-2xl font-bold text-blue-600">{summary.unique_scouts}</p>
                        </div>
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                            <p className="text-xs font-medium text-gray-500">Avg Health Score</p>
                            <p className="text-2xl font-bold text-emerald-600">
                                {summary.avg_health_score ? Number(summary.avg_health_score).toFixed(0) : '—'}%
                            </p>
                        </div>
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                            <p className="text-xs font-medium text-gray-500">Pest Alerts</p>
                            <p className="text-2xl font-bold text-amber-600">{summary.pest_observations}</p>
                        </div>
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                            <p className="text-xs font-medium text-gray-500">Disease Alerts</p>
                            <p className="text-2xl font-bold text-red-600">{summary.disease_observations}</p>
                        </div>
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                            <p className="text-xs font-medium text-gray-500">Follow-ups Needed</p>
                            <p className="text-2xl font-bold text-purple-600">{summary.follow_ups_needed}</p>
                        </div>
                    </div>
                )}

                {/* Filters */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
                    <div className="flex items-center gap-2 mb-4">
                        <FunnelIcon className="w-5 h-5 text-gray-400" />
                        <span className="text-sm font-medium text-gray-700">Filters</span>
                    </div>                        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Start Date</label>
                            <input
                                type="date"
                                value={filters.start_date}
                                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">End Date</label>
                            <input
                                type="date"
                                value={filters.end_date}
                                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Health Status</label>
                            <select
                                value={filters.health_status}
                                onChange={(e) => setFilters({ ...filters, health_status: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                            >
                                <option value="">All</option>
                                <option value="excellent">Excellent</option>
                                <option value="good">Good</option>
                                <option value="fair">Fair</option>
                                <option value="poor">Poor</option>
                                <option value="critical">Critical</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Visit Type</label>
                            <select
                                value={filters.visit_type}
                                onChange={(e) => setFilters({ ...filters, visit_type: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                            >
                                <option value="">All</option>
                                <option value="routine">Routine</option>
                                <option value="followup">Follow-up</option>
                                <option value="emergency">Emergency</option>
                                <option value="training">Training</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Field</label>
                            <select
                                value={filters.field_plot_id}
                                onChange={(e) => setFilters({ ...filters, field_plot_id: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                            >
                                <option value="">All fields</option>
                                {fields.map((field) => (
                                    <option key={field.id} value={field.id}>
                                        {field.name || field.plot_code || `Field #${field.id}`}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="flex items-end">
                            <button
                                onClick={fetchData}
                                className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg flex items-center justify-center gap-2"
                            >
                                <ArrowPathIcon className="w-4 h-4" />
                                Refresh
                            </button>
                        </div>
                    </div>
                </div>

                {/* Entries List */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100">
                        <h2 className="text-lg font-semibold text-gray-900">Field Observations</h2>
                    </div>
                    
                    {loading ? (
                        <div className="p-8 text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto"></div>
                            <p className="text-gray-500 mt-2">Loading entries...</p>
                        </div>
                    ) : entries.length === 0 ? (
                        <div className="p-8 text-center">
                            <CalendarDaysIcon className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                            <p className="text-gray-500">No field diary entries found</p>
                            {canRecordVisits && (
                                <button
                                    onClick={() => setShowForm(true)}
                                    className="mt-4 px-4 py-2 text-emerald-600 font-medium hover:text-emerald-700"
                                >
                                    Create your first entry
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-100">
                            {entries.map((entry) => {
                                const healthColor = getHealthColor(entry.health_status);
                                return (
                                    <div
                                        key={entry.id}
                                        className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
                                        onClick={() => setSelectedEntry(entry)}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-3">
                                                    <span className="text-lg">{VISIT_TYPE_ICONS[entry.visit_type] || '📋'}</span>
                                                    <div>
                                                        <p className="font-semibold text-gray-900">
                                                            {entry.field_name || entry.field_code || entry.cohort_name || 'General Visit'}
                                                        </p>
                                                        <p className="text-sm text-gray-500">
                                                            {entry.scout_name} • {new Date(entry.visit_date).toLocaleDateString()}
                                                            {entry.farmer_name ? ` • ${entry.farmer_name}` : ''}
                                                            {entry.field_crop_type ? ` • ${entry.field_crop_type}` : ''}
                                                        </p>
                                                    </div>
                                                </div>
                                                
                                                <div className="mt-2 flex flex-wrap gap-2">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${healthColor.bg} ${healthColor.text}`}>
                                                        <span className={`w-1.5 h-1.5 rounded-full ${healthColor.dot} mr-1.5`}></span>
                                                        {entry.health_status}
                                                    </span>
                                                    
                                                    {entry.pest_observed && (
                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700">
                                                            🐛 Pest: {entry.pest_type || 'Observed'}
                                                        </span>
                                                    )}
                                                    
                                                    {entry.disease_observed && (
                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700">
                                                            🦠 Disease: {entry.disease_type || 'Observed'}
                                                        </span>
                                                    )}
                                                    
                                                    {entry.follow_up_required && (
                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700">
                                                            📅 Follow-up needed
                                                        </span>
                                                    )}
                                                </div>
                                                
                                                {entry.observations && (
                                                    <p className="mt-2 text-sm text-gray-600 line-clamp-2">
                                                        {entry.observations}
                                                    </p>
                                                )}
                                            </div>
                                            
                                            <div className="ml-4 flex items-center gap-2">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedEntry(entry);
                                                    }}
                                                    className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                                                >
                                                    <EyeIcon className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Create/Edit Form Modal */}
            {(showForm || editingEntry) && (
                <FieldDiaryForm
                    isOpen={showForm || !!editingEntry}
                    onClose={() => {
                        setShowForm(false);
                        setEditingEntry(null);
                    }}
                    onSubmit={editingEntry ? handleUpdateEntry : handleCreateEntry}
                    initialData={editingEntry}
                />
            )}

            {/* Detail Modal */}
            {selectedEntry && (
                <FieldDiaryDetail
                    entry={selectedEntry}
                    isOpen={!!selectedEntry}
                    onClose={() => setSelectedEntry(null)}
                    onEdit={() => {
                        setEditingEntry(selectedEntry);
                        setSelectedEntry(null);
                    }}
                    onDelete={() => handleDeleteEntry(selectedEntry.id)}
                    canEdit={canRecordVisits && (selectedEntry.scout_id === user?.id || isManager)}
                />
            )}
        </div>
    );
};

export default FieldDiaryPage;
